// 📦 AI Order-voorstellen widget — toont mails waar AI een klantbestelling detecteerde
// Verschijnt onder de event-voorstellen in Mail Hub sidebar
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '@/lib/api';
import { format, parseISO } from 'date-fns';
import { nl } from 'date-fns/locale';
import { Package, CheckCircle, X, CaretDown, CaretUp } from '@phosphor-icons/react';

const safeParse = (s) => { try { return parseISO(s); } catch { return new Date(s); } };
const fmtEur = (n) => `€${Number(n || 0).toLocaleString('nl-BE', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

export default function MailOrderProposals() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(true);
  const [busyMid, setBusyMid] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await API.get('/orders/proposals');
      setItems(r.data?.proposals || []);
    } catch { /* silent fail */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  const accept = async (mid) => {
    setBusyMid(mid);
    try {
      const r = await API.post(`/orders/proposals/${mid}/accept`);
      setItems(prev => prev.filter(p => p.mail_id !== mid));
      const docId = r.data?.id;
      if (docId && window.confirm(`✓ Bestelbon ${docId} aangemaakt als concept.\n\nOpen nu het document om te controleren en versturen?`)) {
        navigate(`/documenten/${docId}/bewerken`);
      }
    } catch (e) {
      alert('Accepteren mislukt: ' + (e.response?.data?.detail || e.message));
    }
    setBusyMid(null);
  };

  const dismiss = async (mid) => {
    setBusyMid(mid);
    try {
      await API.post(`/orders/proposals/${mid}/dismiss`);
      setItems(prev => prev.filter(p => p.mail_id !== mid));
    } catch (e) {
      alert('Negeren mislukt: ' + (e.response?.data?.detail || e.message));
    }
    setBusyMid(null);
  };

  if (items.length === 0) return null;

  return (
    <div className="mb-3 bg-gradient-to-br from-emerald-50 to-teal-50 border-2 border-emerald-300 rounded-lg overflow-hidden" data-testid="mail-order-proposals">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-2 py-1.5 text-left hover:bg-emerald-100 transition"
        data-testid="mail-order-proposals-toggle"
      >
        <span className="flex items-center gap-1 text-[11px] font-bold text-emerald-800">
          <Package size={12} weight="fill" />
          AI Bestellingen
          <span className="bg-emerald-600 text-white text-[9px] px-1.5 py-0.5 rounded-full font-bold">{items.length}</span>
        </span>
        {open ? <CaretUp size={11} className="text-emerald-600" /> : <CaretDown size={11} className="text-emerald-600" />}
      </button>
      {open && (
        <div className="px-1.5 pb-1.5 space-y-1 max-h-72 overflow-y-auto">
          {items.map(p => {
            const datum = p.mail_datum ? safeParse(p.mail_datum) : null;
            const lijnenCount = (p.lijnen || []).length;
            return (
              <div
                key={p.mail_id}
                className="bg-white rounded border border-emerald-200 p-1.5 text-[10px] shadow-sm"
                data-testid={`mail-order-proposal-${p.mail_id}`}
              >
                <div className="font-semibold text-gray-800 truncate" title={p.klant_hint || p.mail_from}>
                  📦 {p.klant_hint || p.mail_from}
                </div>
                <div className="text-emerald-700 mt-0.5">
                  {lijnenCount} regel{lijnenCount === 1 ? '' : 's'} • {fmtEur(p.totaal_hint)}
                </div>
                {p.lijnen && p.lijnen.slice(0, 2).map((ln, i) => (
                  <div key={i} className="text-gray-500 truncate" title={ln.omschrijving}>
                    • {ln.aantal} × {ln.omschrijving}
                  </div>
                ))}
                {lijnenCount > 2 && (
                  <div className="text-gray-400 italic">+{lijnenCount - 2} meer...</div>
                )}
                <div className="text-gray-400 truncate mt-0.5">
                  ✉ {p.mail_from} {datum && `• ${format(datum, 'd MMM', { locale: nl })}`}
                </div>
                <div className="flex gap-1 mt-1.5">
                  <button
                    onClick={() => accept(p.mail_id)}
                    disabled={busyMid === p.mail_id}
                    className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white text-[10px] font-semibold py-1 rounded inline-flex items-center justify-center gap-0.5 disabled:opacity-50"
                    title={`Confidence ${Math.round((p.confidence || 0) * 100)}% • ${p.redenering || ''}`}
                    data-testid={`mail-order-proposal-accept-${p.mail_id}`}
                  >
                    <CheckCircle size={10} weight="bold" /> Bestelbon
                  </button>
                  <button
                    onClick={() => dismiss(p.mail_id)}
                    disabled={busyMid === p.mail_id}
                    className="px-1.5 py-1 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded inline-flex items-center justify-center disabled:opacity-50"
                    title="Negeren"
                    data-testid={`mail-order-proposal-dismiss-${p.mail_id}`}
                  >
                    <X size={10} weight="bold" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
